import { Injectable } from "@angular/core";
import { HttpClient, HttpParams, HttpErrorResponse } from "@angular/common/http";
import { Observable, of } from "rxjs";
import { catchError } from "rxjs/operators";
import { AuthenticationService, BaseApiService, EnvironmentConfig } from "../../core/core";

declare const environment: EnvironmentConfig;

export interface Coupon {
  id?: string;
  code: string;
  description?: string;
  discountValue: number;
  expirationDate?: string;
  status: string;
  scope: string;
  accountId?: string;
  isActive?: boolean;
}

export interface CouponValidationResponse {
  isValid: boolean;
  coupon?: Coupon;
  message?: string;
}

@Injectable({
  providedIn: "root",
})
export class CouponService extends BaseApiService<Coupon> {
  constructor(http: HttpClient, auth: AuthenticationService) {
    super(http, environment.ordersApiDomain, "/api/orders/v1/coupon", true, auth, true);
  }

  /**
   * Claims the anonymous cart offer — mints (or returns) the Global "10% off, expires in a week"
   * coupon for the given phone number. Only valid for anonymous carts with items; the discount
   * value and expiry are fixed server-side. Errors propagate so the caller can surface a toast.
   */
  claimAnonymousCartOffer(phoneNumber: string, cartId: string): Observable<{ coupon: Coupon }> {
    return this.httpClient.post<{ coupon: Coupon }>(`${this.url}/anonymous-cart-offer`, { phoneNumber, cartId }, { headers: this.getAuth() });
  }

  validateCoupon(code: string, accountId?: string): Observable<CouponValidationResponse> {
    let params = new HttpParams();
    if (accountId) {
      params = params.set("accountId", accountId);
    }
    return this.httpClient.get<CouponValidationResponse>(`${this.url}/code/${code}/validate`, { params, headers: this.getAuth() }).pipe(
      catchError((error: HttpErrorResponse) => {
        return of({
          isValid: false,
          message: error.error?.message || "Something went wrong. Please try again.",
        });
      })
    );
  }
}
